import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} content - The content to sanitize
 * @param {Object} options - DOMPurify options
 * @returns {string} - Sanitized content
 */
export const sanitizeHTML = (content, options = {}) => {
  if (typeof content !== 'string') return content;
  
  const defaultOptions = {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'br', 'ul', 'ol', 'li', 'span'],
    ALLOWED_ATTR: ['href', 'title', 'target', 'rel'],
    ALLOW_DATA_ATTR: false,
    ...options
  };
  
  return DOMPurify.sanitize(content, defaultOptions);
};

/**
 * Sanitize plain text input - removes all HTML
 * @param {string} content - The content to sanitize
 * @returns {string} - Sanitized plain text
 */
export const sanitizeText = (content) => {
  if (typeof content !== 'string') return content;
  
  // Remove all HTML tags
  return DOMPurify.sanitize(content, { 
    ALLOWED_TAGS: [], 
    ALLOWED_ATTR: [] 
  });
};

/**
 * Sanitize user input for display
 * Escapes HTML entities to prevent injection
 * @param {string} content - The content to sanitize
 * @returns {string} - Escaped content safe for display
 */
export const sanitizeInput = (content) => {
  if (typeof content !== 'string') return content;
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;'
  };
  
  return content.replace(/[&<>"'/]/g, (char) => map[char]);
};

/**
 * Sanitize URL to prevent javascript: and data: injections
 * @param {string} url - The URL to sanitize
 * @returns {string|null} - Sanitized URL or null if invalid
 */
export const sanitizeURL = (url) => {
  if (typeof url !== 'string') return null;
  
  const trimmed = url.trim().toLowerCase();
  
  // Block dangerous protocols
  const dangerousProtocols = ['javascript:', 'data:', 'vbscript:', 'file:'];
  for (const protocol of dangerousProtocols) {
    if (trimmed.startsWith(protocol)) {
      return null;
    }
  }
  
  // Allow http, https, mailto, tel, and relative URLs
  const safeProtocols = ['http://', 'https://', 'mailto:', 'tel:', '/', './'];
  const hasSafeProtocol = safeProtocols.some(p => trimmed.startsWith(p.toLowerCase()));
  
  if (!hasSafeProtocol && !trimmed.startsWith('#') && !trimmed.startsWith('?')) {
    // Relative URL without explicit protocol is OK
    if (!trimmed.includes(':')) {
      return url.trim();
    }
    return null;
  }
  
  return url.trim();
};

/**
 * Sanitize object recursively - useful for API responses
 * @param {Object} obj - Object to sanitize
 * @returns {Object} - Sanitized object
 */
export const sanitizeObject = (obj) => {
  if (obj === null || obj === undefined) return obj;
  
  if (typeof obj === 'string') {
    return sanitizeInput(obj);
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeObject(item));
  }
  
  if (typeof obj === 'object') {
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
      // Sanitize the key as well
      const sanitizedKey = sanitizeInput(key);
      sanitized[sanitizedKey] = sanitizeObject(value);
    }
    return sanitized;
  }
  
  return obj;
};

export default {
  sanitizeHTML,
  sanitizeText,
  sanitizeInput,
  sanitizeURL,
  sanitizeObject
};
